import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Upload, message } from 'antd';
import type { UploadProps, UploadFile } from 'antd/es/upload/interface';
import { CreateUploadedFilePayloadDtoEntityTypeEnum } from 'libs/pinnacle-sdk';
import { storageApi } from 'services/apis';
import FileItem from 'components/ui/file-item';
import FileDragger from 'components/ui/file-dragger';
import { CustomFile } from 'components/features/file-uploader/types';
import { EFileStatus } from 'components/features/file-uploader/enum';
import { useTranslation } from 'react-i18next';
import {
  VALID_FORMAT,
  MAXIMUM_SIZE_IN_MB,
  isValidFormat,
  MAXIMUM_FILE_NUMBER,
} from 'constants/files';
import { handleUpload } from 'services/blob-storage';

interface FileUploaderProps {
  sessionId: string;
  entityType: CreateUploadedFilePayloadDtoEntityTypeEnum;
  fileList: CustomFile[];
  setFileList: React.Dispatch<React.SetStateAction<CustomFile[]>>;
  disabled?: boolean;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  fileList,
  setFileList,
  sessionId,
  entityType,
  disabled,
}) => {
  const { t } = useTranslation();

  const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});
  const uploadSuccessMessageShownRef = useRef(new Set());
  const isMaximumFileNumberErrorShown = useRef(false);

  const getBlobName = useCallback(
    (fileName: string) => `${entityType}/${sessionId}/${fileName}`,
    [entityType, sessionId]
  );

  const handleDownload = useCallback(
    /**
     * if the file comes from the original fileList (CustomFile[]), it could have legacyFilePath
     * which should be prioritized for download
     */
    async (file: UploadFile & Pick<CustomFile, 'legacyFilePath'>) => {
      try {
        const blobName = file.legacyFilePath ?? getBlobName(file.name);
        const { downloadLink } = await storageApi.getDownloadLink({
          blobName,
          isLegacyFile: !!file.legacyFilePath,
        });
        window.open(downloadLink, '_blank');
      } catch {
        message.error(t('file.downloadFailed', { fileName: file.name }));
      }
    },
    [getBlobName, t]
  );

  const handleRemove = useCallback(
    (file: CustomFile) => {
      setFileList((prev) => prev.filter((f) => f.name !== file.name));
      message.success(t('file.fileRemoved', { fileName: file.name }));
    },
    [setFileList, t]
  );

  const simulateUpload = useCallback(
    async (file: CustomFile) => {
      setUploading((prev) => ({ ...prev, [file.uid]: true }));

      try {
        let simulationCompleted = false;

        // Percentage simulation
        const percentageSimulation = new Promise((resolve) => {
          let percent = 0;
          const interval = setInterval(() => {
            if (simulationCompleted) {
              clearInterval(interval);
              return resolve(null);
            }
            percent += 5;

            if (percent > 99) {
              clearInterval(interval);
              resolve(null);
            } else {
              setFileList((prev) =>
                prev.map((f) => (f.name === file.uid ? { ...f, percent } : f))
              );
            }
          }, 200);
        });

        const apiCallPromise: Promise<string> = new Promise(
          /* eslint-disable-next-line no-async-promise-executor */
          async (resolve, reject) => {
            try {
              const fileId = await handleUpload({
                customFile: file,
                entityType,
                fileName: file.name,
                sessionId,
              });
              simulationCompleted = true; // Stop simulation when API call resolves
              setFileList((prev) => {
                return prev.map((f) => {
                  if (f.name === file.uid) {
                    if (!uploadSuccessMessageShownRef.current.has(file.uid)) {
                      message.success(
                        t('file.uploadSuccess', { fileName: file.name })
                      );
                      uploadSuccessMessageShownRef.current.add(file.uid);
                    }
                    return {
                      ...f,
                      status: EFileStatus.DONE,
                      percent: 100,
                      id: fileId,
                    };
                  }
                  return f;
                });
              });
              uploadSuccessMessageShownRef.current.delete(file.uid);
              resolve(fileId);
            } catch (error) {
              reject(error);
            }
          }
        );

        await Promise.all([percentageSimulation, apiCallPromise]);
      } catch {
        setFileList((prev) =>
          prev.map((f) =>
            f.name === file.uid
              ? { ...f, status: EFileStatus.ERROR, percent: 0 }
              : f
          )
        );
        message.error(t('file.uploadFailed', { fileName: file.name }));
      } finally {
        setUploading((prev) => ({ ...prev, [file.uid]: false }));
      }
    },
    [setFileList, entityType, sessionId, t]
  );

  const uploadProps = useMemo<UploadProps>(
    () => ({
      name: 'file',
      multiple: true,
      fileList: fileList as UploadFile[],
      beforeUpload: async (file, currentFileList) => {
        const isExceedMaximumFileNumber =
          currentFileList.length + fileList.length > MAXIMUM_FILE_NUMBER;
        if (isExceedMaximumFileNumber) {
          if (!isMaximumFileNumberErrorShown.current) {
            message.error(
              t('file.maximumFileNumber', {
                maximumNumber: MAXIMUM_FILE_NUMBER,
              })
            );
          }
          isMaximumFileNumberErrorShown.current = true;
          if (currentFileList[currentFileList.length - 1].name === file.name) {
            isMaximumFileNumberErrorShown.current = false;
          }

          return Upload.LIST_IGNORE;
        }

        const isValidFileFormat = isValidFormat(
          file.name.split('.').pop()?.toLowerCase() || ''
        );
        if (!isValidFileFormat) {
          message.error(
            t('file.invalidFormat', {
              fileTypes: VALID_FORMAT.map((f) => f.toUpperCase()).join(', '),
            })
          );
          return Upload.LIST_IGNORE;
        }

        const isLessThanMaximumSize =
          file.size / 1024 / 1024 < MAXIMUM_SIZE_IN_MB;
        if (!isLessThanMaximumSize) {
          message.error(
            t('file.invalidSize', { maximumSize: MAXIMUM_SIZE_IN_MB })
          );
          return Upload.LIST_IGNORE;
        }

        const isFileExist = fileList.some((f) => f.name === file.name);
        if (isFileExist) {
          message.error(t('file.fileExists', { fileName: file.name }));
          return Upload.LIST_IGNORE;
        }

        const uploadFile: CustomFile = {
          originalFile: file,
          status: EFileStatus.UPLOADING,
          percent: 0,
          uid: file.name,
          size: file.size,
          name: file.name,
        };

        setFileList((prev) => [...prev, uploadFile]);
        await simulateUpload(uploadFile);
        return false;
      },
      itemRender: (_, file) => {
        return (
          <FileItem
            file={file as UploadFile}
            handleDownload={handleDownload}
            disabled={disabled}
            uploading={uploading}
            simulateUpload={simulateUpload}
            handleRemove={handleRemove}
          />
        );
      },
    }),
    [
      fileList,
      setFileList,
      simulateUpload,
      t,
      handleDownload,
      disabled,
      uploading,
      handleRemove,
    ]
  );

  return (
    <FileDragger
      fileList={fileList}
      uploadProps={uploadProps}
      disabled={disabled}
    />
  );
};

export default FileUploader;
